import { Injectable, signal } from '@angular/core';
import { type UserDetails } from '#models';

const STORAGE_KEY = 'currentUser';

/**
 * Manages the authenticated user's session in localStorage.
 *
 * The "currentUser" key holds the complete JSON-serialised {@link UserDetails}
 * object returned by GET /v1/auth/me. A reactive signal (`currentUser`) exposes
 * the value so templates and other services can react to login / logout
 * transitions without manual subscriptions.
 *
 * Public API:
 *  - saveCurrentUser(user)  — persists the details and updates the signal.
 *  - getCurrentUser()       — returns the in-memory signal value.
 *  - removeCurrentUser()    — clears localStorage and resets the signal to null.
 */
@Injectable({ providedIn: 'root' })
export class UserSessionService {
    private _currentUser = signal<UserDetails | null>(this._loadFromStorage());
    private _activeRoleId = signal<string | null>(null);

    /** Read-only reactive signal; updates whenever the session changes. */
    readonly currentUser = this._currentUser.asReadonly();

    /** Reactive signal holding the stored active role ID. Null until the user switches roles. */
    readonly activeRoleId = this._activeRoleId.asReadonly();

    /**
     * Persists the full user details to localStorage and updates the reactive signal.
     * Calling this a second time (e.g., to attach the profile photo) replaces the
     * previous entry in-place.
     */
    saveCurrentUser(user: UserDetails): void {
        try {
            // Omit profilePhoto from storage — it is a large base64 blob and is
            // re-fetched from the API on each session start.
            const { profilePhoto: _omit, ...toStore } = user;
            localStorage.setItem(STORAGE_KEY, JSON.stringify(toStore));
        } catch (err) {
            // localStorage quota exceeded or private-browsing restriction — non-fatal.
            console.warn('[UserSessionService] Could not persist user details:', err);
        }
        this._currentUser.set(user);
    }

    /**
     * Returns the current in-memory {@link UserDetails}, or null when no session
     * exists.  Prefer reading the `currentUser` signal in reactive contexts.
     */
    getCurrentUser(): UserDetails | null {
        return this._currentUser();
    }

    /**
     * Removes the user details from localStorage and resets the signal to null.
     * Called during logout before triggering the Entra ID redirect.
     */
    removeCurrentUser(): void {
        try {
            localStorage.removeItem(STORAGE_KEY);
        } catch {
            // Ignore — storage may already be cleared.
        }
        this._currentUser.set(null);
        this.removeActiveRoleId();
    }

    /** Updates the in-memory active role signal. Seeded from GET /auth/me; updated on role switch. */
    saveActiveRoleId(roleId: string): void {
        this._activeRoleId.set(roleId);
    }

    /** Clears the active role signal. Called on logout. */
    removeActiveRoleId(): void {
        this._activeRoleId.set(null);
    }

    // ── Private helpers ───────────────────────────────────────────────────────

    /**
     * Attempts to deserialise the stored JSON on service initialisation.
     * Returns null on any parsing failure so the app continues gracefully.
     */
    private _loadFromStorage(): UserDetails | null {
        try {
            const raw = localStorage.getItem(STORAGE_KEY);
            if (!raw) return null;
            return JSON.parse(raw) as UserDetails;
        } catch {
            // Corrupted or tampered storage entry — start fresh.
            return null;
        }
    }
}
